import {
  chmodSync,
  existsSync,
  mkdirSync,
  readFileSync,
  renameSync,
  unlinkSync,
  writeFileSync,
} from 'node:fs';
import { homedir } from 'node:os';
import { basename, dirname, join } from 'node:path';

const DEFAULT_PORT = 7497;

export interface DeviceConfig {
  id: string;
  host: string;
  port: number;
  apiKey?: string;
  scheme?: 'ws' | 'wss';
  apiPath?: string;
  platform?: string;
  version?: string;
  aliases?: string[];
}

export interface CliConfig {
  devices: DeviceConfig[];
  defaultDevice?: string;
}

export interface ConfigPaths {
  configPath: string;
  credentialsPath: string;
}

export function defaultConfigPath(): string {
  return join(
    process.env.XDG_CONFIG_HOME ?? join(homedir(), '.config'),
    'zaparoo-cli',
    'config.json',
  );
}

export function defaultCredentialsPath(): string {
  return join(
    process.env.XDG_CONFIG_HOME ?? join(homedir(), '.config'),
    'zaparoo-cli',
    'credentials.json',
  );
}

export function resolvePaths(configPath?: string, credentialsPath?: string): ConfigPaths {
  return {
    configPath: configPath ?? process.env.ZAPAROO_CONFIG_PATH ?? defaultConfigPath(),
    credentialsPath:
      credentialsPath ?? process.env.ZAPAROO_CREDENTIALS_PATH ?? defaultCredentialsPath(),
  };
}

export function parseDevice(raw: string, apiKey?: string): DeviceConfig {
  const input = /^[a-z]+:\/\//i.test(raw) ? raw : `ws://${raw}`;
  let url: URL;
  try {
    url = new URL(input);
  } catch {
    throw new Error(`Invalid device "${raw}"`);
  }
  if (url.protocol !== 'ws:' && url.protocol !== 'wss:') {
    throw new Error(`Invalid device scheme "${url.protocol}"`);
  }
  const host = url.hostname.replace(/^\[|\]$/g, '');
  const port = url.port ? Number.parseInt(url.port, 10) : DEFAULT_PORT;
  if (!host || Number.isNaN(port) || port < 1 || port > 65535) {
    throw new Error(`Invalid device "${raw}"`);
  }
  const scheme = url.protocol.slice(0, -1) as 'ws' | 'wss';
  const defaultPath = url.pathname === '/' ? undefined : url.pathname;
  const displayHost = host.includes(':') ? `[${host}]` : host;
  return {
    id: `${displayHost}:${port}`,
    host,
    port,
    apiKey,
    scheme,
    apiPath: defaultPath,
  };
}

export function parseDeviceList(raw: string, keys = ''): DeviceConfig[] {
  const hosts = raw
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  const apiKeys = keys.split(',').map((part) => part.trim() || undefined);
  return hosts.map((host, index) => parseDevice(host, apiKeys[index]));
}

export function loadCliConfig(configPath?: string): CliConfig {
  const path = configPath ?? defaultConfigPath();
  let fileConfig: Partial<CliConfig> = {};
  if (existsSync(path)) {
    try {
      fileConfig = JSON.parse(readFileSync(path, 'utf8')) as Partial<CliConfig>;
    } catch (error) {
      throw new Error(
        `Configuration file ${path} is malformed: ${error instanceof Error ? error.message : String(error)}`,
      );
    }
  }
  const envDevices = process.env.ZAPAROO_DEVICES;
  const envKeys = process.env.ZAPAROO_KEYS ?? '';
  const devices = envDevices ? parseDeviceList(envDevices, envKeys) : (fileConfig.devices ?? []);
  const defaultDevice = process.env.ZAPAROO_DEFAULT_DEVICE ?? fileConfig.defaultDevice;
  return { devices, defaultDevice };
}

export function saveDeviceMetadata(
  device: DeviceConfig,
  metadata: Pick<DeviceConfig, 'platform' | 'version'>,
  configPath?: string,
): boolean {
  if (process.env.ZAPAROO_DEVICES) return false;
  const config = loadCliConfig(configPath);
  const index = config.devices.findIndex(
    (configured) =>
      configured.id === device.id ||
      configured.aliases?.includes(device.id) ||
      device.aliases?.includes(configured.id),
  );
  if (index < 0) return false;
  const current = config.devices[index];
  if (current.platform === metadata.platform && current.version === metadata.version) return false;
  config.devices[index] = { ...current, ...metadata };
  saveCliConfig(config, configPath);
  return true;
}

export function saveCliConfig(config: CliConfig, configPath?: string): void {
  const path = configPath ?? defaultConfigPath();
  const dir = dirname(path);
  if (!existsSync(dir)) mkdirSync(dir, { recursive: true, mode: 0o700 });
  const temporary = join(dir, `.${basename(path)}.${process.pid}.${Date.now()}.tmp`);
  try {
    writeFileSync(temporary, JSON.stringify(config, null, 2), { mode: 0o600 });
    renameSync(temporary, path);
    chmodSync(path, 0o600);
  } catch (error) {
    try {
      unlinkSync(temporary);
    } catch {
      // Missing temporary file needs no cleanup.
    }
    throw error;
  }
}
